import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  description: string;
  action: () => void;
  category: string;
}

interface UseKeyboardShortcutsOptions {
  shortcuts?: KeyboardShortcut[];
  enabled?: boolean;
}

export function useKeyboardShortcuts({ shortcuts = [], enabled = true }: UseKeyboardShortcutsOptions = {}) {
  const navigate = useNavigate();

  const defaultShortcuts: KeyboardShortcut[] = [
    {
      key: "k",
      meta: true,
      ctrl: true,
      description: "Abrir Command Palette",
      action: () => {}, // Handled by CommandPalette component
      category: "Geral",
    },
    {
      key: "h",
      meta: true,
      ctrl: true,
      description: "Ir para Início",
      action: () => navigate("/"),
      category: "Navegação",
    },
    {
      key: "d",
      meta: true,
      ctrl: true,
      description: "Ir para Dashboard",
      action: () => navigate("/dashboard"),
      category: "Navegação",
    },
    {
      key: "f",
      meta: true,
      ctrl: true,
      description: "Ir para Ferramentas",
      action: () => navigate("/ferramentas"),
      category: "Navegação",
    },
    {
      key: "c",
      meta: true,
      ctrl: true,
      description: "Ir para Conversas",
      action: () => navigate("/conversations"),
      category: "Navegação",
    },
    {
      key: "g",
      meta: true,
      ctrl: true,
      description: "Ir para Guias",
      action: () => navigate("/guias"),
      category: "Navegação",
    },
    {
      key: "?",
      shift: true,
      description: "Mostrar Atalhos",
      action: () => {}, // Handled by help modal trigger
      category: "Geral",
    },
  ];

  const allShortcuts = [...defaultShortcuts, ...shortcuts];

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in input fields
    const target = event.target as HTMLElement;
    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable
    ) {
      // Allow Cmd/Ctrl+K even in input fields
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        return;
      }
      return;
    }

    allShortcuts.forEach((shortcut) => {
      const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatch = shortcut.ctrl ? event.ctrlKey : true;
      const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
      const altMatch = shortcut.alt ? event.altKey : !event.altKey;
      const metaMatch = shortcut.meta ? event.metaKey : true;

      // For Cmd+K, match either Cmd or Ctrl
      if (shortcut.key === "k" && (shortcut.meta || shortcut.ctrl)) {
        if (keyMatch && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          return;
        }
        return;
      }

      if (keyMatch && ctrlMatch && shiftMatch && altMatch && metaMatch) {
        if (shortcut.key !== "k") { // Cmd+K is handled by CommandPalette
          event.preventDefault();
          shortcut.action();
        }
      }
    });
  }, [allShortcuts, enabled]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return { shortcuts: allShortcuts };
}

export function getShortcutDisplay(shortcut: KeyboardShortcut): string {
  const parts: string[] = [];
  
  if (shortcut.meta || shortcut.ctrl) {
    parts.push(navigator.platform.includes("Mac") ? "⌘" : "Ctrl");
  }
  if (shortcut.shift) parts.push("⇧");
  if (shortcut.alt) parts.push(navigator.platform.includes("Mac") ? "⌥" : "Alt");
  
  parts.push(shortcut.key.toUpperCase());
  
  return parts.join(" + ");
}
